import { forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { InsuranceForm } from '@makemydeal/dr-shared-components';
import type { InsuranceFormRef } from '@makemydeal/dr-shared-components';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { useCustomerCardSelector } from '../hooks/useCustomerCardSelector';
import * as customerCardSelectors from '../store/selectors';
import { fetchInsuranceProviders } from '../store/actionCreators';
import type { VehicleInsurance } from '@makemydeal/dr-platform-types';
import type { CustomerCardCapabilities, OnAnalyticsEvent } from '../types';

type InsuranceTabProps = {
    vehicleInsuranceInfo?: VehicleInsurance | null;
    isVehicleInsuranceUpdated?: boolean;
    capabilities?: CustomerCardCapabilities;
    onChange?: (isDirty: boolean, isValid: boolean) => void;
    onContractingErrorsAlert?: (hasErrors: boolean) => void;
    isAnyOperationInProgress?: boolean;
    dealId?: string;
    hostApp?: 'MV' | 'SCI';
    onAnalyticsEvent?: OnAnalyticsEvent;
};

const InsuranceTab = forwardRef<InsuranceFormRef, InsuranceTabProps>((props, ref): JSX.Element => {
    const {
        vehicleInsuranceInfo,
        isVehicleInsuranceUpdated,
        capabilities,
        onChange,
        onContractingErrorsAlert,
        isAnyOperationInProgress = false,
        dealId,
        hostApp,
        onAnalyticsEvent
    } = props;
    const dispatch = useDispatch();
    const insuranceProviders = useCustomerCardSelector(customerCardSelectors.getInsuranceProviders);
    const buyer = useCustomerCardSelector(customerCardSelectors.getBuyer);
    const contractApiErroredVehicleInsurance = useCustomerCardSelector(customerCardSelectors.getContractApiErroredVehicleInsurance);
    const isAdditionalInsuranceFieldsEnabled = useSelector(featureToggleSelectors.isAdditionalInsuranceFieldsEnabled);
    const dealerId = useSelector(customerCardSelectors.getDealerId);

    return (
        <div data-testid="customer-card-insurance-form">
            <InsuranceForm
                ref={ref}
                vehicleInsuranceInfo={vehicleInsuranceInfo}
                isVehicleInsuranceUpdated={isVehicleInsuranceUpdated}
                buyerState={buyer?.state}
                isLocked={capabilities?.readOnly}
                isContractingContext={Boolean(capabilities?.contractValidation) && !isAnyOperationInProgress}
                contractApiErroredVehicleInsurance={contractApiErroredVehicleInsurance}
                insuranceProviders={insuranceProviders}
                getInsuranceProviders={() => dispatch(fetchInsuranceProviders())}
                isAdditionalInsuranceFieldsEnabled={isAdditionalInsuranceFieldsEnabled}
                onChange={onChange}
                onContractingErrorsAlert={onContractingErrorsAlert}
                hostApp={hostApp}
                logContext={{ ...(dealId && { dealXgId: dealId }), ...(dealerId != null && { dealerId }) }}
                onProviderInteraction={onAnalyticsEvent}
            />
        </div>
    );
});
InsuranceTab.displayName = 'InsuranceTab';

export default InsuranceTab;
